/**
 * iOS PWA Install Prompt
 * Shows instructions for iOS users to add the app to home screen
 */
import React from 'react';
import { X, Share, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';

const IOSInstallPrompt = ({ onDismiss }) => {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-md p-6 animate-in slide-in-from-bottom duration-500 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Install The Uplift</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDismiss}
            className="rounded-full -mr-2"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* App icon preview */}
        <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
            U
          </div>
          <div>
            <p className="font-semibold">The Uplift</p>
            <p className="text-sm text-muted-foreground">Add to Home Screen for the best experience</p>
          </div>
        </div>

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <span className="text-green-600">✓</span>
            </div>
            <span>Native push notifications for calls & messages</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <span className="text-green-600">✓</span>
            </div>
            <span>Full screen experience</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <span className="text-green-600">✓</span>
            </div>
            <span>Quick access from your home screen</span>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">
            How to install:
          </p>
          <div className="space-y-3 text-sm text-blue-700 dark:text-blue-300">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div className="flex items-center gap-2">
                <span>Tap the</span>
                <Share className="w-4 h-4" />
                <span>Share button below</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div className="flex items-center gap-2">
                <span>Scroll and tap</span>
                <PlusSquare className="w-4 h-4" />
                <span>"Add to Home Screen"</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-xs font-bold">
                3
              </div>
              <span>Tap "Add" to confirm</span>
            </div>
          </div>
        </div>

        {/* Dismiss button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={onDismiss}
        >
          Maybe Later
        </Button>

        {/* Arrow pointing to share button */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full pt-4">
          <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white dark:border-t-[#1a1a1a] animate-bounce" />
        </div>
      </div>
    </div>
  );
};

export default IOSInstallPrompt;
